import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TabletSmartphone, Trash2, Pencil, Lock, LockOpen, Loader2, RefreshCw, Download,
  Smartphone, Monitor, Ban, ShieldCheck, Eye, EyeOff, Search, Printer, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuthStore } from '@/stores';
import { DeviceBranchAssign } from '@/components/pos/DeviceBranchAssign';
import { getDeviceId } from '@/lib/deviceHeartbeat';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PrinterDiagnosticsTile from '@/components/pos/PrinterDiagnosticsTile';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const SHELL_LABELS = { android_app: 'Android App', pwa: 'Installed PWA', browser: 'Browser' };
const CONN_LABELS = { usb: 'USB', bluetooth: 'Bluetooth', network: 'Network/IP', lan: 'Network/IP', airprint: 'AirPrint', epos: 'ePOS', print_bridge: 'Print Bridge' };

function timeAgo(iso) {
  try {
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 90) return 'just now';
    if (s < 3600) return `${Math.round(s / 60)} min ago`;
    if (s < 86400) return `${Math.round(s / 3600)} h ago`;
    return `${Math.round(s / 86400)} d ago`;
  } catch { return '—'; }
}

export default function PosDevicesPage() {
  const navigate = useNavigate();
  const me = useAuthStore((st) => st.user);
  const canAssign = !!me?.is_super_admin || ['admin', 'owner'].includes((me?.role || '').toLowerCase());
  const [devices, setDevices] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [label, setLabel] = useState('');
  const [apkInfo, setApkInfo] = useState(null);
  const [search, setSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');

  const thisDeviceId = getDeviceId();
  const terminalBranchId = (() => { try { return localStorage.getItem('pos_terminal_branch_id') || ''; } catch { return ''; } })();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/cashier/devices');
      setDevices(data.devices || []);
    } catch { toast.error('Failed to load devices'); }
    setLoading(false);
  }, []);

  const loadPrinters = useCallback(async () => {
    try { const { data } = await api.get('/cashier/printers'); setPrinters(data.printers || data || []); }
    catch { setPrinters([]); }
  }, []);

  const loadApkInfo = useCallback(async () => {
    try { const { data } = await api.get('/downloads/apk/info'); setApkInfo(data); }
    catch { setApkInfo({ exists: false }); }
  }, []);

  useEffect(() => { load(); loadPrinters(); loadApkInfo(); }, [load, loadPrinters, loadApkInfo]);

  useEffect(() => {
    const onFocus = () => loadApkInfo();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadApkInfo]);

  const rename = async (id) => {
    try { await api.put(`/cashier/devices/${id}`, { label }); toast.success('Device renamed'); setEditing(null); load(); }
    catch { toast.error('Rename failed'); }
  };

  const forceKiosk = async (id, mode) => {
    try {
      await api.put(`/cashier/devices/${id}`, { kiosk_override: mode });
      toast.success(`Kiosk will turn ${mode.toUpperCase()} on the device's next check-in (within ~2 min)`);
      load();
    } catch { toast.error('Failed to set remote kiosk'); }
  };

  const setRevoked = async (id, revoked) => {
    if (revoked && !window.confirm('Revoke this device? It will be signed out and blocked from logging in until you unblock it. No sales or data are deleted.')) return;
    try {
      await api.put(`/cashier/devices/${id}`, { revoked });
      toast.success(revoked ? 'Device revoked — access blocked' : 'Device unblocked — access restored');
      load();
    } catch { toast.error(revoked ? 'Revoke failed' : 'Unblock failed'); }
  };

  const setHidden = async (id, hidden) => {
    try { await api.put(`/cashier/devices/${id}`, { hidden }); toast.success(hidden ? 'Device hidden' : 'Device shown'); load(); }
    catch { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this device from the list? It will reappear on its next heartbeat (use Revoke to block access instead).')) return;
    try { await api.delete(`/cashier/devices/${id}`); toast.success('Device removed'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const branchOptions = useMemo(() => {
    const m = new Map();
    devices.forEach(d => { if (d.branch_id) m.set(d.branch_id, d.branch_name || d.branch_id); });
    return Array.from(m.entries());
  }, [devices]);

  const thisDevice = useMemo(() => devices.find(d => d.device_id === thisDeviceId), [devices, thisDeviceId]);
  const thisPrinter = useMemo(() => {
    const list = printers.filter(p => !terminalBranchId || p.branch_id === terminalBranchId || !p.branch_id);
    return list.find(p => p.is_default) || list[0] || null;
  }, [printers, terminalBranchId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return devices.filter(d => {
      if (statusFilter === 'revoked') { if (!d.revoked) return false; }
      else if (statusFilter === 'hidden') { if (!d.hidden) return false; }
      else if (statusFilter === 'online') { if (!d.online || d.revoked) return false; }
      else if (statusFilter === 'offline') { if (d.online || d.revoked) return false; }
      else if (statusFilter === 'active') { if (d.hidden) return false; } // default: hide the hidden ones
      if (branchFilter !== 'all' && d.branch_id !== branchFilter) return false;
      if (q) {
        const hay = `${d.label || ''} ${d.device_id} ${d.branch_name || ''} ${d.last_user || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [devices, search, branchFilter, statusFilter]);

  const counts = useMemo(() => ({
    total: devices.length,
    online: devices.filter(d => d.online && !d.revoked).length,
    revoked: devices.filter(d => d.revoked).length,
    hidden: devices.filter(d => d.hidden).length,
  }), [devices]);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="pos-devices-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold font-outfit flex items-center gap-2">
              <TabletSmartphone className="text-orange-500" size={24} /> POS Devices
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Every device that runs the POS — status, branch and last activity. Devices register automatically on their first POS session.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => { load(); loadPrinters(); }} data-testid="devices-refresh-btn">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </div>

        {/* This Terminal */}
        <Card className="border border-orange-200 dark:border-orange-900/40 shadow-sm bg-gradient-to-br from-orange-50/60 to-amber-50/40 dark:from-orange-950/20 dark:to-amber-950/10" data-testid="this-terminal-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><Monitor size={16} className="text-orange-500" /> This Terminal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Terminal ID</div>
                <div className="font-mono text-xs mt-1 break-all" data-testid="this-terminal-id">{thisDeviceId}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><MapPin size={11} /> Assigned Branch</div>
                <div className="text-sm font-medium mt-1" data-testid="this-terminal-branch">
                  {thisDevice?.branch_name || (terminalBranchId ? 'Set — refresh to resolve' : 'Not set')}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Printer size={11} /> Printer</div>
                <div className="text-sm font-medium mt-1" data-testid="this-terminal-printer">
                  {thisPrinter ? (
                    <span>{thisPrinter.name || 'Printer'} <Badge variant="outline" className="text-[9px] ml-1">{CONN_LABELS[thisPrinter.connection_type] || thisPrinter.connection_type || thisPrinter.type || '—'}</Badge></span>
                  ) : <span className="text-muted-foreground">None configured</span>}
                </div>
              </div>
              <div className="flex items-end">
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => navigate('/printer-setup')} data-testid="this-terminal-printer-setup-btn">
                  <Printer size={13} className="mr-1" /> Printer Setup
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {apkInfo?.exists && (
          <Card className="border-0 shadow-sm bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20" data-testid="apk-download-tile">
            <CardContent className="py-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-orange-500/10 flex items-center justify-center shrink-0">
                    <Smartphone className="text-orange-500" size={22} />
                  </div>
                  <div>
                    <div className="font-semibold text-sm flex items-center gap-2">
                      Smarto Android App
                      <Badge variant="outline" className="text-[10px]">{apkInfo.size_mb} MB</Badge>
                      {apkInfo.version && (<Badge variant="secondary" className="text-[10px] font-mono" data-testid="apk-version-badge">v.{apkInfo.version}</Badge>)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">Side-load on any POS tablet. Built {timeAgo(apkInfo.modified_at)}.</div>
                  </div>
                </div>
                <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
                  onClick={() => {
                    const base = process.env.REACT_APP_BACKEND_URL;
                    const ver = apkInfo.version ? `?ver=${apkInfo.version}` : '';
                    window.open(`${base}/api/downloads/apk${ver}`, '_blank');
                  }}
                  data-testid="apk-download-btn">
                  <Download size={14} /> Download APK
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <PrinterDiagnosticsTile />

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-sm font-semibold">
                {counts.total} device{counts.total === 1 ? '' : 's'}
                <span className="text-xs font-normal text-muted-foreground ml-2">
                  {counts.online} online{counts.revoked ? ` · ${counts.revoked} revoked` : ''}{counts.hidden ? ` · ${counts.hidden} hidden` : ''}
                </span>
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search device, branch, user…"
                    className="h-8 w-48 pl-7 text-xs" data-testid="devices-search" />
                </div>
                <Select value={branchFilter} onValueChange={setBranchFilter}>
                  <SelectTrigger className="h-8 w-40 text-xs" data-testid="devices-branch-filter"><SelectValue placeholder="Branch" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All branches</SelectItem>
                    {branchOptions.map(([id, name]) => (<SelectItem key={id} value={id}>{name}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-8 w-36 text-xs" data-testid="devices-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active (default)</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                    <SelectItem value="revoked">Revoked</SelectItem>
                    <SelectItem value="hidden">Hidden</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center" data-testid="devices-empty">
                {devices.length === 0 ? 'No devices yet. Open the Cashier POS on a tablet — it will register itself here automatically.' : 'No devices match the current filters.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="devices-table">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      {['Device', 'Type', 'Last seen at', 'Locked to branch', 'Status', 'Last User', 'Last Seen', ''].map(h => (
                        <th key={h} className="text-left p-3 font-semibold text-xs text-muted-foreground uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(d => {
                      const isThis = d.device_id === thisDeviceId;
                      return (
                      <tr key={d.device_id} className={`border-b border-border hover:bg-muted/50 ${d.revoked ? 'bg-red-50/40 dark:bg-red-950/10' : ''}`} data-testid={`device-row-${d.device_id}`}>
                        <td className="p-3 text-sm">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${d.revoked ? 'bg-red-500' : d.online ? 'bg-emerald-500' : 'bg-stone-300'}`} title={d.revoked ? 'Revoked' : d.online ? 'Online' : 'Offline'} />
                            {editing === d.device_id ? (
                              <span className="flex gap-1 items-center">
                                <Input value={label} onChange={(e) => setLabel(e.target.value)} className="h-7 w-36 text-xs" autoFocus data-testid="device-label-input" />
                                <Button size="sm" className="h-7 px-2 text-xs" onClick={() => rename(d.device_id)} data-testid="device-label-save">Save</Button>
                              </span>
                            ) : (
                              <span className="font-medium flex items-center gap-1.5">
                                {d.label || `Device ${d.device_id.slice(0, 6)}`}
                                {isThis && <Badge variant="outline" className="text-[9px] border-orange-300 text-orange-600">This device</Badge>}
                              </span>
                            )}
                          </div>
                          <div className="font-mono text-[10px] text-muted-foreground mt-0.5 pl-4">{d.device_id}</div>
                        </td>
                        <td className="p-3"><Badge variant="outline" className="text-[10px]">{SHELL_LABELS[d.shell] || d.shell}</Badge></td>
                        <td className="p-3 text-sm">{d.branch_name}</td>
                        <td className="p-3"><DeviceBranchAssign device={d} canAssign={canAssign} onSaved={load} /></td>
                        <td className="p-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {d.revoked ? (
                              <Badge className="bg-red-100 text-red-700 border-red-200 gap-1 text-[10px]" data-testid={`device-revoked-${d.device_id}`}><Ban size={10} /> Revoked</Badge>
                            ) : d.online ? (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Online</Badge>
                            ) : (
                              <Badge variant="outline" className="text-muted-foreground text-[10px]">Offline</Badge>
                            )}
                            {d.kiosk_enabled && <Badge className="bg-sky-100 text-sky-700 border-sky-200 gap-1 text-[9px]"><Lock size={9} /> Locked</Badge>}
                            {d.kiosk_override && (<Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[9px]" data-testid={`kiosk-pending-${d.device_id}`}>pending → {d.kiosk_override.toUpperCase()}</Badge>)}
                            {d.hidden && <Badge variant="outline" className="text-[9px] text-muted-foreground gap-1"><EyeOff size={9} /> Hidden</Badge>}
                          </div>
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">{d.last_user || '—'}</td>
                        <td className="p-3 text-sm text-muted-foreground">{timeAgo(d.last_seen)}</td>
                        <td className="p-3 text-right">
                          <div className="flex gap-1 justify-end flex-wrap">
                            {d.revoked ? (
                              <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-emerald-600 border-emerald-200" title="Unblock — restore access"
                                onClick={() => setRevoked(d.device_id, false)} data-testid={`device-unblock-${d.device_id}`}>
                                <ShieldCheck size={12} className="mr-1" /> Unblock
                              </Button>
                            ) : (
                              <>
                                {(() => {
                                  const effectiveOn = d.kiosk_override ? d.kiosk_override === 'on' : d.kiosk_enabled;
                                  return effectiveOn ? (
                                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" title="Lock = force kiosk fullscreen OFF"
                                      onClick={() => forceKiosk(d.device_id, 'off')} data-testid={`kiosk-force-off-${d.device_id}`}>
                                      <LockOpen size={12} className="mr-1" /> Unlock
                                    </Button>
                                  ) : (
                                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" title="Lock = force kiosk fullscreen ON (does not block access)"
                                      onClick={() => forceKiosk(d.device_id, 'on')} data-testid={`kiosk-force-on-${d.device_id}`}>
                                      <Lock size={12} className="mr-1" /> Lock
                                    </Button>
                                  );
                                })()}
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" title="Revoke = block this device from POS access"
                                  onClick={() => setRevoked(d.device_id, true)} data-testid={`device-revoke-${d.device_id}`}>
                                  <Ban size={13} />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title={d.hidden ? 'Unhide' : 'Hide from list (does not block)'}
                                  onClick={() => setHidden(d.device_id, !d.hidden)} data-testid={`device-hide-${d.device_id}`}>
                                  {d.hidden ? <Eye size={13} /> : <EyeOff size={13} />}
                                </Button>
                              </>
                            )}
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Rename"
                              onClick={() => { setEditing(d.device_id); setLabel(d.label || ''); }} data-testid={`device-rename-${d.device_id}`}>
                              <Pencil size={13} />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" title="Remove from list (reappears on next heartbeat)"
                              onClick={() => remove(d.device_id)} data-testid={`device-delete-${d.device_id}`}>
                              <Trash2 size={13} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
